import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Dialog, Typography, IconButton } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";

import LoggedNav from "../components/LoggedNav";
import LinkPayment from "./LinkPayment";
import ErrorDialog from "../components/ErrorDialog";

import reminder from "../images/icons/reminder.png";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ;

const CHECKLIST_ITEMS = [
  {
    name: "inp1",
    label: "Please confirm that the Burners and oven if used are turned off",
  },
  {
    name: "inp2",
    label: "Please confirm that the dish washer is loaded",
  },
  {
    name: "inp3",
    label: "Please confirm the kitchen is clean",
  },
];

// Helper to strip HTML tags if needed
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

function Reminder(props) {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [bookerName, setBookerName] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [validationError, setValidationError] = useState("");
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
    // State for reusable error dialog
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorDialogTitle, setErrorDialogTitle] = useState("");
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [errorDialogAction, setErrorDialogAction] = useState(null);
  const [errorDialogActionUrl, setErrorDialogActionUrl] = useState("");
  const [errorDialogButtonText, setErrorDialogButtonText] = useState("OK");
  const [showContactLink, setShowContactLink] = useState(false);

  const saveMerchantIdToBackend = useCallback(async (merchantId, email) => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("logged_user_id");

      if (!token || !userId) {
        window.location.href = "/chef/sign-in";
        return;
      }

      const res = await fetch(`${API_BASE_URL}/user/add_chef_account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          merchantId,
          Email: email,
          merchantIdInPayPal: merchantId,
          permissionsGranted: true,
          accountStatus: "active",
          consentStatus: "active",
          isEmailConfirmed: "true",
          returnMessage: "Onboarding complete",
        }),
      });
      const data = await res.json();

      if (res.status === 401) {
        localStorage.clear();
        window.location.href = "/chef/sign-in";
      } else if (!res.ok || !data.success) {
        console.error(data.message || "Failed to save Merchant ID.");
      }
    } catch (err) {
      console.error("Save merchant error:", err);
    }
  }, []);

  useEffect(() => {
    document.title = "Reminder on Meal Prep Completion | CHEF | CHEF REPUBLIC";

    const savedBookingId = localStorage.getItem("reminder_bookingId");
    const savedBookerName = localStorage.getItem("reminder_bookerName");

    const finalBookingId = state?.bookingId || savedBookingId;
    const finalBookerName = state?.bookerName || savedBookerName;

    if (!finalBookingId || !finalBookerName) {
      navigate(-1);
      return;
    }

    setBookingId(finalBookingId);
    setBookerName(finalBookerName);
    localStorage.setItem("reminder_bookingId", finalBookingId);
    localStorage.setItem("reminder_bookerName", finalBookerName);
  }, [state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  const handleCheckboxChange = (name, checked) => {
    setCheckedItems((prev) => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    if (!recaptchaToken) {
      setValidationError("Please complete the reCAPTCHA");
      return false;
    }
    for (const item of CHECKLIST_ITEMS) {
      if (!checkedItems[item.name]) {
        setValidationError(`Please confirm: ${item.label}`);
        return false;
      }
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}status/chef_meal_preparation_complete`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            booking_id: bookingId.toString(),
            status: "6",
          }),
        }
      );
      const data = await res.json();

      // Success case
      if (data.success === true) {
        localStorage.removeItem("reminder_bookingId");
        localStorage.removeItem("reminder_bookerName");
        navigate("/chef/order-list", { state: { bookingId, bookerName } });
        return;
      }

      // 1. Email not verified
      if (data.email_verified === false) {
        setErrorDialogTitle("Email Confirmation Required");
        setErrorDialogMessage(data.message || "Chef email is not confirmed. Cannot process payment.");
        setErrorDialogActionUrl("https://www.paypal.com");
        setErrorDialogButtonText("Confirm Email Address");
        setShowErrorDialog(true);
        return;
      }

      // 2. Payments not receivable
      if (data.payments_receivable === false) {
        setErrorDialogTitle("Payment Account Issue");
        setErrorDialogMessage(data.message || "Chef account is not eligible to receive payments.");
        setErrorDialogActionUrl("https://www.paypal.com");
        setErrorDialogButtonText("OK");
        setShowErrorDialog(true);
        return;
      }

      // 3. BD not found (existing)
      if (data.bd_found === false) {
        setShowPaymentPopup(true);
        return;
      }

      // 4. Payment failed
      if (data.payment_failed === true) {
        setErrorDialogTitle("Payment Failed");
        setErrorDialogMessage(data.message || "Payment failed. Please try again.");
        setErrorDialogButtonText("OK");
        setShowErrorDialog(true);
        return;
      }

      // 5. Other failure
      if (data.success === false) {
        setErrorDialogTitle("Error");
        setErrorDialogMessage(data.message || "Kindly contact the Chef Republic admin for assistance.");
        setErrorDialogButtonText("OK");
        setShowContactLink(true);
        setShowErrorDialog(true);
        return;
      }
    } catch (err) {
      console.error("Meal prep submission error:", err);
      setValidationError("Error while submitting. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <LoggedNav customer={props.customer} />

      <div
        className="container d-flex flex-column justify-content-center align-items-center"
        style={{ width: "95%", margin: "auto", height: "80vh" }}
      >
        <div
          className="my-5 p-3"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#ff5e41",
            borderRadius: "50%",
            height: "100px",
            width: "100px",
          }}
        >
          <img src={reminder} alt="reminder" style={{ width: "95%" }} />
        </div>

        <h1 className="fw-bold">Reminder</h1>

        <form onSubmit={handleSubmit}>
          <div className="d-flex flex-column">
            {CHECKLIST_ITEMS.map(({ name, label }) => (
              <div className="form-check m-3" key={name}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  name={name}
                  checked={!!checkedItems[name]}
                  onChange={(e) => handleCheckboxChange(name, e.target.checked)}
                />
                <label className="form-check-label">{label}</label>
              </div>
            ))}
          </div>

          {validationError && (
            <p className="text-danger text-center">{validationError}</p>
          )}

          <div className="mb-3 d-flex justify-content-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={setRecaptchaToken}
            />
          </div>

          <div className="d-flex justify-content-center">
            <button type="submit" className="btn-orange" disabled={isSubmitting}>
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Confirm"
              )}
            </button>
           
          </div>
        </form>
         <p style={{ fontSize: "0.5rem", color: "#7C7C7C" , mt: 2}}>
  I agree to accept and fulfill bookings only through Chef Republic for any clients introduced on the platform as outlined in the Terms and Conditions
</p>
      </div>

      {/* Payment popup for linking PayPal account */}
      <Dialog open={showPaymentPopup} onClose={() => setShowPaymentPopup(false)}>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "16px", mb: 2 }}>
            Please link your PayPal Business account to accept payments
          </Typography>
          <LinkPayment />
        </Box>
      </Dialog>

      {/* Reusable Error Dialog */}
      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={errorDialogTitle}
        message={errorDialogMessage}
        showActionButton={true}
        actionButtonText={errorDialogButtonText}
        actionUrl={errorDialogActionUrl}
        showContactLink={showContactLink}
      />
    </>
  );
}

export default Reminder;